import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ContractTemplateService } from '@app/+authenticated/+employees/templates/contract-template/services/contract-template.service';
import { activeLocale } from '@app/+authenticated/shared/locale/locale.helper';
import { ValidationService } from '@app/forms/validation.service';
import { getEmployeeSortDirection, getEmployeeSortField } from '@app/reducers/account/account-service.selectors';
import { selectAccountCountryCode } from '@app/reducers/account/account.selector';
import { AppState } from '@app/reducers/interfaces';
import { getAuthenticatedUser } from '@app/reducers/orm/employee/employee.selectors';
import { LanguageStartup } from '@app/startup/language-startup.service';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { featureFlag, FeatureService } from '@sb/shared/feature';
import { AvatarComponent } from '@sb/ui/avatar';
import { BadgeComponent } from '@sb/ui/badge';
import { ButtonComponent } from '@sb/ui/button';
import { DialogRef, SbDialogModule, SbDialogService } from '@sb/ui/dialog';
import { LaddaDirective } from '@sb/ui/ladda';
import { SelectComponent, SelectItem } from '@sb/ui/select';
import { isAfterOrSame } from '@shiftbase-com/ts-shared/utilities';
import { combineLatest, finalize, firstValueFrom } from 'rxjs';

import { supportedHireCountryCode } from '../../constants/new-hires.const';
import {
  AddNewHirePayload,
  DeactivatedEmployee,
  HireInviteSentResult,
  NewHire,
  NewHireVariables,
} from '../../models/new-hires.model';
import {
  germanTriggerControls,
  useEmploymentCategoryOptions,
  useGermanTriggerControlsSync,
} from '../../services/hire-german-triggers.helper';
import { useHireSubmitErrorHandler } from '../../services/hire-submit-error.helper';
import { HireService } from '../../services/hire.service';
import { NewHiresService } from '../../services/new-hires.service';
import { RehireService } from '../../services/re-hire.service';
import { HireFormComponent } from '../components/hire-form/hire-form.component';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'sb-rehire-begin-flow-modal',
  standalone: true,
  imports: [
    TranslatePipe,
    ButtonComponent,
    ReactiveFormsModule,
    SbDialogModule,
    SelectComponent,
    AvatarComponent,
    BadgeComponent,
    HireFormComponent,
    LaddaDirective,
  ],
  templateUrl: './rehire-begin-flow-modal.component.html',
  styleUrl: './rehire-begin-flow-modal.component.scss',
})
export class RehireBeginFlowModalComponent implements OnInit {
  #dialogRef = inject(DialogRef<HireInviteSentResult | undefined>);
  #destroyRef = inject(DestroyRef);
  #newHiresService = inject(NewHiresService);
  #hireService = inject(HireService);
  #rehireService = inject(RehireService);
  #contractTemplateService = inject(ContractTemplateService);
  #languageStartup = inject(LanguageStartup);
  #dialogService = inject(SbDialogService);
  #translateService = inject(TranslateService);
  #store = inject<Store<AppState>>(Store);
  #featureService = inject(FeatureService);
  #handleHireSubmitError = useHireSubmitErrorHandler();

  loading = signal(false);
  state = signal<'rehire-select' | 'form'>('rehire-select');

  #germanTriggers = germanTriggerControls();

  form = new FormGroup({
    first_name: new FormControl('', [Validators.required, Validators.maxLength(35)]),
    prefix_name: new FormControl('', [Validators.maxLength(10)]),
    last_name: new FormControl('', [Validators.required, Validators.maxLength(50)]),
    email: new FormControl('', [Validators.required, Validators.email, Validators.maxLength(50)]),
    phone: new FormControl(''),
    job_title: new FormControl('', [Validators.required, Validators.maxLength(30)]),
    start_date: new FormControl<string | null>(null, [Validators.required]),
    country: new FormControl(supportedHireCountryCode(this.#store.selectSignal(selectAccountCountryCode)()), [
      Validators.required,
    ]),
    locale: new FormControl(activeLocale(), [Validators.required]),
    team_id: new FormControl('', [Validators.required]),
    requires_guardian_signature: new FormControl(false),
    guardian_signature_count: new FormControl(1),
    ...this.#germanTriggers,
  });

  guardianSignatureEnabled = this.#featureService.isEnabled(featureFlag.TMP_GUARDIAN_SIGNATURE);
  guardianCountEnabled = this.#featureService.isEnabled(featureFlag.TMP_GUARDIAN_SIGNATURE_COUNT);
  #germanReleaseEnabled = this.#featureService.isEnabled(featureFlag.TMP_GERMAN_RELEASE);
  employmentCategories = useEmploymentCategoryOptions(this.#germanReleaseEnabled);

  #country = toSignal(this.form.controls.country.valueChanges, {
    initialValue: this.form.controls.country.value,
  });

  isGermanHire = computed(() => this.#germanReleaseEnabled && this.#country() === 'DE');

  languages = this.#languageStartup.getAvailableLanguages();
  hasContractTemplates = true;
  teams = this.#hireService.teamsTree;
  inactiveEmployeesItems: (SelectItem & {
    employee: DeactivatedEmployee;
    hasActiveContract: boolean;
    hasActiveRehire: boolean;
  })[] = [];
  rehireEmployeeControl = new FormControl<string>('');

  constructor() {
    useGermanTriggerControlsSync(this.#germanTriggers, this.isGermanHire);
  }

  ngOnInit() {
    this.#getContractTemplates();
    this.#initEmailValidator();
    this.#loadInactiveEmployees();
  }

  #initEmailValidator() {
    this.#store
      .select(getAuthenticatedUser)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((authenticated) => {
        const userEmail = authenticated?.email;
        this.form.controls.email?.addValidators(ValidationService.emailMatchValidator(userEmail));
        this.form.controls.email?.updateValueAndValidity();
      });
  }

  #loadInactiveEmployees() {
    const today = new Date().toISOString().split('T')[0];
    combineLatest([
      this.#rehireService.getInactiveEmployees(),
      this.#store.select(getEmployeeSortField),
      this.#store.select(getEmployeeSortDirection),
    ])
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(([res, sortField, sortDirection]) => {
        const sortedEmployees = this.#hireService.sortEmployees<DeactivatedEmployee>(res, sortField, sortDirection);
        this.inactiveEmployeesItems = sortedEmployees.map((employee) => {
          const fullName = [employee.first_name, employee.prefix_name, employee.last_name].filter(Boolean).join(' ');
          return {
            text: fullName,
            label: fullName,
            value: employee.id,
            employee,
            hasActiveContract: isAfterOrSame(employee.end_date, today),
            hasActiveRehire: !!employee.active_rehire,
          };
        });
      });
  }

  #getContractTemplates() {
    void this.#contractTemplateService.getContractTemplates().subscribe((contractTemplates) => {
      this.hasContractTemplates = !!contractTemplates?.length;
    });
  }

  async onSelectRehireEmployee() {
    const employeeId = this.rehireEmployeeControl.value;
    const selectedItem = this.inactiveEmployeesItems.find((item) => item.value === employeeId);
    const employee = selectedItem?.employee;
    if (!employee) return;

    if (selectedItem.hasActiveRehire) {
      const confirmed = await this.#confirmActiveRehire();
      if (!confirmed) return;
    }

    this.form.patchValue({
      first_name: employee.first_name,
      prefix_name: employee.prefix_name || '',
      last_name: employee.last_name,
      email: employee.email || '',
      phone: employee.phone || '',
    });
    this.state.set('form');
  }

  async #confirmActiveRehire(): Promise<boolean> {
    const dialogRef = this.#dialogService.openConfirm({
      type: 'danger',
      title: this.#translateService.instant('Send a new invite?'),
      description: this.#translateService.instant(
        'This employee has already submitted their details. Sending a new invite will cancel their current submission and require them to start over.',
      ),
      primary: {
        text: this.#translateService.instant('Send new invite'),
      },
      secondary: {
        text: this.#translateService.instant('Cancel'),
      },
    });

    const result = await firstValueFrom(dialogRef.closed);
    return !!result?.confirmed;
  }

  submit() {
    this.form.markAllAsTouched();

    if (!this.form.valid) {
      return;
    }

    // form.value excludes disabled controls, so the German triggers are present only for German rehires.
    const { team_id, requires_guardian_signature, guardian_signature_count, ...variables } = this.form.value;
    const rawGuardianCount = requires_guardian_signature ? (guardian_signature_count ?? 1) : 0;
    // Clamp to single-guardian when the count feature is off (the select is hidden, but the control
    // could still hold 2), so a legacy rehire never submits a count that should be unavailable.
    const guardianSignatureCount = this.guardianCountEnabled ? rawGuardianCount : Math.min(rawGuardianCount, 1);
    const payload: AddNewHirePayload = {
      team_id: team_id!,
      variables: variables as NewHireVariables,
      employee_id: this.rehireEmployeeControl.value!,
      ...(this.guardianSignatureEnabled && { guardian_signature_count: guardianSignatureCount }),
    };

    this.loading.set(true);

    this.#newHiresService
      .reHire(payload)
      .pipe(
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe({
        next: ({ id }: NewHire) => {
          // Without an id the invite-sent dialog has no contract-setup page to link to.
          this.#dialogRef.close(
            id ? { newHireId: id, firstName: this.form.controls.first_name.value ?? '' } : undefined,
          );
        },
        error: (submitError: unknown) => this.#handleHireSubmitError(submitError, this.form),
      });
  }
}
